import { coco, ResourceType } from '@cotera/api';
import { z } from 'zod';
import type { PendingChatSubmission } from '../../chat/models/pending-chat-submission';
import type {
  ChatSeedBuildInput,
  ChatSeedStrategy,
} from '../../chat-seed/types';
import { workflowChatSeedChrome } from './chrome';
import { workflowChatSeedPreSubmit } from './pre-submit';
import {
  WORKFLOW_SEED_TRIGGER_QUERY_KEYS,
  encodeSeedTriggerParams,
} from './seed-trigger';

const {
  COCO_OPEN_WORKFLOW_TOOL_KEY,
  COCO_READ_WORKFLOW_GRAPH_TOOL_KEY,
  COCO_CREATE_WORKFLOW_TOOL_KEY,
  COCO_ADD_WORKFLOW_NODE_TOOL_KEY,
  COCO_UPDATE_WORKFLOW_NODE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_NODE_TOOL_KEY,
  COCO_ADD_WORKFLOW_EDGE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_EDGE_TOOL_KEY,
  COCO_ADD_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_ADD_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
} = coco;

// Seeding any workflow tool activates the `coco/edit-workflows` skill, which
// carries the builder instructions; the full granular set is seeded so the
// chat can build the graph directly (no workflow-editor sub-agent).
export const WORKFLOW_BUILDER_TOOL_KEYS: string[] = [
  COCO_OPEN_WORKFLOW_TOOL_KEY,
  COCO_READ_WORKFLOW_GRAPH_TOOL_KEY,
  COCO_CREATE_WORKFLOW_TOOL_KEY,
  COCO_ADD_WORKFLOW_NODE_TOOL_KEY,
  COCO_UPDATE_WORKFLOW_NODE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_NODE_TOOL_KEY,
  COCO_ADD_WORKFLOW_EDGE_TOOL_KEY,
  COCO_DELETE_WORKFLOW_EDGE_TOOL_KEY,
  COCO_ADD_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_CRON_TRIGGER_TOOL_KEY,
  COCO_ADD_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
  COCO_DELETE_WORKFLOW_DATASET_TRIGGER_TOOL_KEY,
];

const uuidSchema = z.string().uuid();

export const WORKFLOW_SEED_TYPE = 'workflow';

export const NEW_WORKFLOW_CHAT_DISPLAY_TITLE = 'New Workflow';

function parseArtifactId(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get('artifactId');
  if (raw === null) {
    return null;
  }
  const parsed = uuidSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export const workflowChatSeedStrategy: ChatSeedStrategy = {
  type: WORKFLOW_SEED_TYPE,
  artifactResourceType: ResourceType.Workflow,
  extraStripQueryKeys: WORKFLOW_SEED_TRIGGER_QUERY_KEYS,
  getChatChrome: () => workflowChatSeedChrome,
  preSubmit: workflowChatSeedPreSubmit,

  buildUrlSearchParams: (input: ChatSeedBuildInput): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('seed', WORKFLOW_SEED_TYPE);
    params.set('artifactId', input.artifactId);
    if (input.isNew === true) {
      params.set('new', '1');
    }
    if (input.folderId !== undefined) {
      params.set('folderId', input.folderId);
    }
    if (input.seedTrigger !== undefined) {
      encodeSeedTriggerParams(params, input.seedTrigger);
    }
    return params;
  },

  buildPendingContext: (input: ChatSeedBuildInput) => {
    const data: Record<string, unknown> = {
      workflowId: input.artifactId,
      ...(input.isNew === true && { isNew: true }),
    };

    return {
      ...(input.isNew === true && {
        displayTitle: NEW_WORKFLOW_CHAT_DISPLAY_TITLE,
      }),
      initialArtifacts: [
        {
          artifactType: 'workflow' as const,
          artifactId: input.artifactId,
          data,
        },
      ],
      initialToolKeys: WORKFLOW_BUILDER_TOOL_KEYS,
    };
  },

  parseFromUrl: (
    searchParams: URLSearchParams
  ): PendingChatSubmission | null => {
    if (searchParams.get('seed') !== WORKFLOW_SEED_TYPE) {
      return null;
    }

    const artifactId = parseArtifactId(searchParams);
    if (artifactId === null) {
      return null;
    }

    const isNew = searchParams.get('new') === '1';

    return {
      ...workflowChatSeedStrategy.buildPendingContext({ artifactId, isNew }),
      seedOnly: true,
    };
  },
};
